import { effect, inject, Injectable, signal } from '@angular/core';
import { UserStateService } from '@daisi/services/user-state/user-state.service';
import { fromEvent } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class AudioNotificationService {
  private readonly userStateService = inject(UserStateService);

  readonly hasUnreadNotification = signal<boolean>(false);

  private notificationSound = new Audio('assets/daisi-notification-sound-v1.mp3');

  // We don't play any sounds in the first few seconds the app was loaded, as all events will be considered 'new'
  private readonly appLoadedTime = Date.now();
  private readonly noSoundsForTheFirstXMilliSeconds = 10_000;

  private lastNotificationMillis = 0;

  constructor() {
    this.notificationSound.load();

    // play a sounds when audio notifications are enabled
    effect(() => {
      if (this.userStateService.state.notificationSoundsEnabled()) {
        this.playNotificationSound();
      }
    });

    effect(() => {
      this.notificationSound.volume = this.userStateService.state.notificationSoundVolume();
      this.playNotificationSound();
    });

    // Listen for visibility changes to reset hasUnreadNotification when tab becomes active
    fromEvent(document, 'visibilitychange').subscribe(() => {
      if (!document.hidden) {
        // Tab is now active/visible, clear unread notifications flag
        this.hasUnreadNotification.set(false);
      }
    });
  }

  onNewIncident(): void {
    if (this.lastNotificationMillis + 5_000 > Date.now()) {
      // The last notification was less than 5 seconds ago, so we skip this one
      return;
    }

    this.lastNotificationMillis = Date.now();
    this.playNotificationSound();
  }

  private playNotificationSound(): void {
    if (
      // User doesn't want sounds
      !this.userStateService.state.notificationSoundsEnabled() ||
      // The app is still loading
      this.appLoadedTime + this.noSoundsForTheFirstXMilliSeconds > Date.now()
    ) {
      return;
    }

    this.notificationSound.currentTime = 0; // Herstart geluid bij herhaling
    this.notificationSound.play().catch((err) => {
      console.warn('Audio notification could not be played:', err);
    });

    // If tab is not visible/active when sound plays
    if (document.hidden) {
      this.hasUnreadNotification.set(true);
    }
  }
}
